import React from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Check if a user is an admin
 * @param {string} userId - The user ID to check
 * @returns {Promise<boolean>} - True if user is admin
 */
export async function isAdmin(userId) {
  if (!userId) return false;
  
  try {
    const adminDoc = await getDoc(doc(db, 'admins', userId));
    return adminDoc.exists();
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

/**
 * React hook to check if current user is admin
 * Uses a listener for real-time updates
 */
export function useIsAdmin(userId) {
  const [adminStatus, setAdminStatus] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!userId) {
      setAdminStatus(false);
      setLoading(false);
      return;
    }

    const adminDocRef = doc(db, 'admins', userId);
    
    // Log for debugging
    console.log('🔍 Checking admin status for UID:', userId);
    console.log('🔍 Looking for document at path: admins/' + userId);
    
    const unsubscribe = onSnapshot(
      adminDocRef,
      (snapshot) => {
        const exists = snapshot.exists();
        console.log('🔍 Admin document exists:', exists);
        if (exists) {
          console.log('🔍 Admin document data:', snapshot.data());
        }
        setAdminStatus(exists);
        setLoading(false);
      },
      (error) => {
        console.error('❌ Error checking admin status:', error);
        console.error('❌ Error code:', error.code);
        console.error('❌ Error message:', error.message);
        // Don't silently handle errors - log them for debugging
        setAdminStatus(false);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  return { isAdmin: adminStatus, loading };
}

